import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowSize } from '@custom-react-hooks/all';
import { useAppConfig } from '../context/AppConfigContext';
import { toCssLength } from '../config';
import { CarouselLogo, fetchHomeWheelLogos } from '../services/carouselLogos';
import styles from './HomeShowcaseWheel.module.css';

const TAU = 2 * Math.PI;
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const normalizeAngle = (angle: number) => ((angle % TAU) + TAU) % TAU;

interface TrimmedLogo {
  image: HTMLImageElement;
  sourceX: number;
  sourceY: number;
  sourceWidth: number;
  sourceHeight: number;
  backgroundColor?: string;
  backgroundColor2?: string;
}

const toHex = (value: number) => value.toString(16).padStart(2, '0');

const rgbToHex = (red: number, green: number, blue: number) =>
  `#${toHex(red)}${toHex(green)}${toHex(blue)}`;

const adjustChannel = (value: number, amount: number) =>
  Math.round(Math.min(255, Math.max(0, value + (255 - value) * amount)));

const darkenChannel = (value: number, amount: number) =>
  Math.round(Math.min(255, Math.max(0, value * (1 - amount))));

const buildPalette = (red: number, green: number, blue: number) => {
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  const secondaryColor =
    luminance > 0.7
      ? rgbToHex(
          darkenChannel(red, 0.22),
          darkenChannel(green, 0.22),
          darkenChannel(blue, 0.22)
        )
      : rgbToHex(
          adjustChannel(red, 0.18),
          adjustChannel(green, 0.18),
          adjustChannel(blue, 0.18)
        );

  return {
    backgroundColor: rgbToHex(red, green, blue),
    backgroundColor2: secondaryColor,
  };
};

const getTrimmedLogo = (image: HTMLImageElement): TrimmedLogo => {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  if (!width || !height) {
    return {
      image,
      sourceX: 0,
      sourceY: 0,
      sourceWidth: width || 1,
      sourceHeight: height || 1,
    };
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    if (!ctx) {
      throw new Error('Canvas context unavailable.');
    }

    ctx.drawImage(image, 0, 0, width, height);
    const { data } = ctx.getImageData(0, 0, width, height);

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    let redTotal = 0;
    let greenTotal = 0;
    let blueTotal = 0;
    let weightTotal = 0;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const pixelIndex = (y * width + x) * 4;
        const red = data[pixelIndex];
        const green = data[pixelIndex + 1];
        const blue = data[pixelIndex + 2];
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha <= 8) {
          continue;
        }

        redTotal += red * alpha;
        greenTotal += green * alpha;
        blueTotal += blue * alpha;
        weightTotal += alpha;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }

    if (maxX < minX || maxY < minY) {
      return {
        image,
        sourceX: 0,
        sourceY: 0,
        sourceWidth: width,
        sourceHeight: height,
      };
    }

    const palette =
      weightTotal > 0
        ? buildPalette(
            Math.round(redTotal / weightTotal),
            Math.round(greenTotal / weightTotal),
            Math.round(blueTotal / weightTotal)
          )
        : {};

    return {
      image,
      sourceX: minX,
      sourceY: minY,
      sourceWidth: maxX - minX + 1,
      sourceHeight: maxY - minY + 1,
      ...palette,
    };
  } catch {
    return {
      image,
      sourceX: 0,
      sourceY: 0,
      sourceWidth: width,
      sourceHeight: height,
    };
  }
};

const normalizeColor = (value: string | undefined) => {
  const color = value?.trim();
  if (!color) {
    return color || '';
  }

  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', color) ? color : '';
  }

  return color;
};

const parseHexColor = (value: string) => {
  const normalized = value.trim();
  if (!normalized.startsWith('#')) {
    return null;
  }

  const hex = normalized.slice(1);
  if (hex.length === 3) {
    const [r, g, b] = hex.split('');
    return {
      red: Number.parseInt(`${r}${r}`, 16),
      green: Number.parseInt(`${g}${g}`, 16),
      blue: Number.parseInt(`${b}${b}`, 16),
    };
  }

  if (hex.length === 6) {
    return {
      red: Number.parseInt(hex.slice(0, 2), 16),
      green: Number.parseInt(hex.slice(2, 4), 16),
      blue: Number.parseInt(hex.slice(4, 6), 16),
    };
  }

  return null;
};

const parseRgbColor = (value: string) => {
  const normalized = value.trim();
  const match = normalized.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+\s*)?\)$/i
  );

  if (!match) {
    return null;
  }

  return {
    red: Number.parseInt(match[1], 10),
    green: Number.parseInt(match[2], 10),
    blue: Number.parseInt(match[3], 10),
  };
};

const getReadableTextColor = (backgroundColor: string) => {
  const rgb = parseHexColor(backgroundColor) || parseRgbColor(backgroundColor);
  if (!rgb) {
    return '#161616';
  }

  const luminance =
    (0.2126 * rgb.red + 0.7152 * rgb.green + 0.0722 * rgb.blue) / 255;

  return luminance > 0.62 ? '#161616' : '#ffffff';
};

const mergeTrimmedLogo = (current: TrimmedLogo, next: TrimmedLogo): TrimmedLogo => ({
  image: current.image,
  sourceX: next.sourceWidth ? next.sourceX : current.sourceX,
  sourceY: next.sourceHeight ? next.sourceY : current.sourceY,
  sourceWidth: next.sourceWidth || current.sourceWidth,
  sourceHeight: next.sourceHeight || current.sourceHeight,
  backgroundColor: current.backgroundColor || next.backgroundColor,
  backgroundColor2: current.backgroundColor2 || next.backgroundColor2,
});

const HomeShowcaseWheel: React.FC = () => {
  const { config } = useAppConfig();
  const { width } = useWindowSize();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>(0);
  const lastFrameTimeRef = useRef<number>(0);
  const angleRef = useRef<number>(0);
  const activeIndexRef = useRef(0);
  const logoCacheRef = useRef<Map<string, TrimmedLogo | null>>(new Map());

  const [logos, setLogos] = useState<CarouselLogo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [logoVersion, setLogoVersion] = useState(0);
  const [centerImageFailed, setCenterImageFailed] = useState(false);

  const canvasSize = Math.min(590, width > 700 ? 590 : Math.max(width - 24, 300));
  const sectors = logos;
  const total = sectors.length;
  const arc = total > 0 ? TAU / total : TAU;

  const getIndexForAngle = useCallback(
    (angle: number) => {
      if (!total) {
        return 0;
      }

      const normalizedAngle = normalizeAngle(angle);
      return Math.floor(total - (normalizedAngle / TAU) * total) % total;
    },
    [total]
  );

  const drawContainedImage = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      logo: TrimmedLogo,
      centerX: number,
      centerY: number,
      maxWidth: number,
      maxHeight: number
    ) => {
      const { image, sourceX, sourceY, sourceWidth, sourceHeight } = logo;

      if (!sourceWidth || !sourceHeight) {
        return;
      }

      const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
      const drawWidth = sourceWidth * scale;
      const drawHeight = sourceHeight * scale;

      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        centerX - drawWidth / 2,
        centerY - drawHeight / 2,
        drawWidth,
        drawHeight
      );
    },
    []
  );

  const drawLogoInSegment = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      logo: TrimmedLogo,
      radius: number,
      sectorArc: number,
      scale = 1
    ) => {
      const { sourceWidth, sourceHeight } = logo;
      if (!sourceWidth || !sourceHeight) {
        return;
      }

      const tangentHalfArc = Math.tan(sectorArc / 2);
      if (tangentHalfArc <= 0) {
        return;
      }

      const aspectRatio = sourceWidth / sourceHeight;
      const outerRadius = radius * 0.94;
      const centerX = radius * 0.57;
      const maxHeightFromAngle =
        (2 * centerX * tangentHalfArc) / (1 + aspectRatio * tangentHalfArc);

      const quadraticA = (aspectRatio * aspectRatio + 1) / 4;
      const quadraticB = centerX * aspectRatio;
      const quadraticC = centerX * centerX - outerRadius * outerRadius;
      const discriminant = quadraticB * quadraticB - 4 * quadraticA * quadraticC;

      if (discriminant <= 0) {
        return;
      }

      const maxHeightFromRadius =
        (-quadraticB + Math.sqrt(discriminant)) / (2 * quadraticA);
      const maxHeight = Math.max(0, Math.min(maxHeightFromAngle, maxHeightFromRadius));

      if (!maxHeight) {
        return;
      }

      const maxWidth = maxHeight * aspectRatio;
      const occupancy = clamp(scale, 0.4, 1.5);

      drawContainedImage(
        ctx,
        logo,
        centerX,
        0,
        Math.max(maxWidth * occupancy, 1),
        Math.max(maxHeight * occupancy, 1)
      );
    },
    [drawContainedImage]
  );

  const drawWheel = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const radius = canvasSize / 2;
      const separatorWidth = clamp(canvasSize * 0.008, 2, 6);

      ctx.clearRect(0, 0, canvasSize, canvasSize);

      sectors.forEach((sector, index) => {
        const startAngle = arc * index;
        const logo = logoCacheRef.current.get(sector.logoUrl) ?? null;
        const manualColorStart = normalizeColor(sector.wheelBackgroundColor);
        const manualColorEnd = normalizeColor(sector.wheelBackgroundColor2);
        const autoColorStart = normalizeColor(logo?.backgroundColor);
        const autoColorEnd = normalizeColor(logo?.backgroundColor2);
        const colorStart =
          manualColorStart ||
          autoColorStart ||
          (index % 2 === 0 ? 'rgba(255, 255, 255, 0.96)' : 'rgba(255, 248, 225, 0.96)');
        const colorEnd =
          manualColorEnd ||
          autoColorEnd ||
          (index % 2 === 0 ? normalizeColor(config.primaryColor) : normalizeColor(config.accentColor)) ||
          colorStart;
        const usesManualFlatColor = Boolean(manualColorStart) && !manualColorEnd;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(radius, radius);
        ctx.arc(radius, radius, radius, startAngle, startAngle + arc);
        ctx.closePath();
        ctx.clip();

        if (usesManualFlatColor) {
          ctx.fillStyle = manualColorStart;
        } else {
          const gradient = ctx.createLinearGradient(0, 0, canvasSize, canvasSize);
          gradient.addColorStop(0, colorStart);
          gradient.addColorStop(1, colorEnd);
          ctx.fillStyle = gradient;
        }
        ctx.fillRect(0, 0, canvasSize, canvasSize);

        if (logo) {
          ctx.translate(radius, radius);
          ctx.rotate(startAngle + arc / 2);
          drawLogoInSegment(ctx, logo, radius, arc, sector.wheelLogoScale ?? 0.96);

          if (!usesManualFlatColor) {
            const overlay = ctx.createLinearGradient(0, -radius, radius, radius);
            overlay.addColorStop(0, 'rgba(255, 255, 255, 0.12)');
            overlay.addColorStop(1, 'rgba(255, 255, 255, 0.02)');
            ctx.fillStyle = overlay;
            ctx.fillRect(0, -radius, radius, radius * 2);
          }
        }

        ctx.restore();

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(radius, radius);
        ctx.arc(radius, radius, radius, startAngle, startAngle + arc);
        ctx.closePath();
        ctx.lineWidth = separatorWidth;
        ctx.strokeStyle = 'rgba(243, 187, 26, 0.9)';
        ctx.stroke();
        ctx.restore();
      });

      ctx.save();
      ctx.beginPath();
      ctx.arc(radius, radius, radius - separatorWidth / 2, 0, TAU);
      ctx.lineWidth = clamp(canvasSize * 0.015, 6, 10);
      ctx.strokeStyle = 'rgba(255, 215, 0, 0.95)';
      ctx.stroke();
      ctx.restore();
    },
    [arc, canvasSize, config.accentColor, config.primaryColor, drawLogoInSegment, sectors]
  );

  useEffect(() => {
    let active = true;

    const loadLogos = async () => {
      try {
        const remoteLogos = await fetchHomeWheelLogos();
        if (active && remoteLogos.length > 0) {
          setLogos(remoteLogos);
        }
      } catch (error) {
        console.warn('Failed to load home wheel logos from Firestore.', error);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadLogos();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    sectors.forEach((sector) => {
      if (!sector.logoUrl || logoCacheRef.current.has(sector.logoUrl)) {
        return;
      }

      const image = new Image();
      image.onload = () => {
        if (cancelled) {
          return;
        }

        const analyzedLogo = getTrimmedLogo(image);
        logoCacheRef.current.set(sector.logoUrl, analyzedLogo);
        setLogoVersion((value) => value + 1);

        if (analyzedLogo.backgroundColor) {
          return;
        }

        const probeImage = new Image();
        probeImage.crossOrigin = 'anonymous';
        probeImage.onload = () => {
          if (cancelled) {
            return;
          }

          const detectedLogo = getTrimmedLogo(probeImage);
          const currentLogo = logoCacheRef.current.get(sector.logoUrl);
          if (!currentLogo) {
            return;
          }

          logoCacheRef.current.set(
            sector.logoUrl,
            mergeTrimmedLogo(currentLogo, detectedLogo)
          );
          setLogoVersion((value) => value + 1);
        };
        probeImage.onerror = () => {};
        probeImage.src = sector.logoUrl;
      };
      image.onerror = () => {
        if (cancelled) {
          return;
        }

        logoCacheRef.current.set(sector.logoUrl, null);
        setLogoVersion((value) => value + 1);
      };
      image.src = sector.logoUrl;
    });

    return () => {
      cancelled = true;
    };
  }, [sectors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || total === 0) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    drawWheel(ctx);
  }, [canvasSize, drawWheel, logoVersion, total]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || total === 0) {
      return;
    }

    const updateRotation = (angle: number) => {
      angleRef.current = angle;
      canvas.style.transform = `rotate(${angle - Math.PI / 2}rad)`;

      const nextIndex = getIndexForAngle(angle);
      if (nextIndex !== activeIndexRef.current) {
        activeIndexRef.current = nextIndex;
        setActiveIndex(nextIndex);
      }
    };

    const animate = (timestamp: number) => {
      if (!lastFrameTimeRef.current) {
        lastFrameTimeRef.current = timestamp;
      }

      const deltaSeconds = (timestamp - lastFrameTimeRef.current) / 1000;
      lastFrameTimeRef.current = timestamp;
      updateRotation(angleRef.current + deltaSeconds * 0.45);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    updateRotation(angleRef.current);
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      lastFrameTimeRef.current = 0;
    };
  }, [getIndexForAngle, total]);

  const activeLogo = sectors[activeIndex] ?? null;
  const activeTrimmedLogo = activeLogo
    ? logoCacheRef.current.get(activeLogo.logoUrl) ?? null
    : null;
  const centerImageUrl = useMemo(
    () => config.homeWheelCenterImageUrl?.trim() || config.logoUrl?.trim() || '',
    [config.homeWheelCenterImageUrl, config.logoUrl]
  );
  const centerImageStyle = useMemo(
    () => ({
      width: toCssLength(config.homeWheelCenterImageWidth, '100%'),
      height: toCssLength(config.homeWheelCenterImageHeight, '100%'),
      objectFit: 'contain' as const,
    }),
    [config.homeWheelCenterImageHeight, config.homeWheelCenterImageWidth]
  );
  const centerBackgroundStart = useMemo(
    () =>
      normalizeColor(activeLogo?.centerBackgroundColor) ||
      normalizeColor(activeLogo?.wheelBackgroundColor) ||
      normalizeColor(activeTrimmedLogo?.backgroundColor) ||
      'rgba(255, 255, 255, 0.98)',
    [
      activeLogo?.centerBackgroundColor,
      activeLogo?.wheelBackgroundColor,
      activeTrimmedLogo?.backgroundColor,
    ]
  );
  const centerBackgroundEnd = useMemo(
    () =>
      normalizeColor(activeLogo?.centerBackgroundColor2) ||
      normalizeColor(activeLogo?.wheelBackgroundColor2) ||
      normalizeColor(activeTrimmedLogo?.backgroundColor2) ||
      centerBackgroundStart,
    [
      activeLogo?.centerBackgroundColor2,
      activeLogo?.wheelBackgroundColor2,
      activeTrimmedLogo?.backgroundColor2,
      centerBackgroundStart,
    ]
  );
  const centerStyle = useMemo(
    () => ({
      background:
        centerBackgroundStart === centerBackgroundEnd
          ? centerBackgroundStart
          : `linear-gradient(145deg, ${centerBackgroundStart}, ${centerBackgroundEnd})`,
    }),
    [centerBackgroundEnd, centerBackgroundStart]
  );
  const centerLabelStyle = useMemo(
    () => ({
      color: getReadableTextColor(centerBackgroundStart),
    }),
    [centerBackgroundStart]
  );
  const centerLabel = config.brandName || activeLogo?.name || 'Brand';

  useEffect(() => {
    setCenterImageFailed(false);
  }, [centerImageUrl]);

  return (
    <div
      className={styles.wrapper}
      style={{ width: canvasSize, height: canvasSize }}
      aria-label="Brand showcase wheel"
    >
      <div className={styles.surface}>
        {isLoading ? (
          <div className={styles.skeleton} />
        ) : (
          <canvas
            ref={canvasRef}
            width={canvasSize}
            height={canvasSize}
            className={styles.canvas}
          />
        )}
      </div>
      <div className={styles.center} style={centerStyle}>
        {centerImageUrl && !centerImageFailed ? (
          <img
            src={centerImageUrl}
            alt={centerLabel}
            className={styles.centerImage}
            style={centerImageStyle}
            onError={() => setCenterImageFailed(true)}
          />
        ) : (
          <span className={styles.centerLabel} style={centerLabelStyle}>
            {centerLabel}
          </span>
        )}
      </div>
    </div>
  );
};

export default HomeShowcaseWheel;
